import { CloseButton, Tabs } from '@mantine/core';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Colors, usePrompt } from '../../../../ui';
import { Position } from '../../../../ui/Position';
import { Shadows } from '../../../../ui/Standards';
import { BlockWheel } from '../../../canvas/tooling/ToolLayer';
import { useComponentObserver } from '../../../canvas/visualization/ComponentObserver';
import { PopoutWidget } from '../../../canvas/workspace/widgets/WidgetInterface';
import { useResolve } from '../../../Container';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { JiraCard } from '../JiraCard';
import { JiraIssueCommentsView } from './JiraIssueCommentsView';
import { JiraIssueDescriptionView } from './JiraIssueDescriptionView';
import { JiraIssueDetailsView } from './JiraIssueDetailsView';
import { jiraSidebarClose } from './JiraSidebarCommands';

const Frame = styled.div`
    position: relative;
    border-radius: .75rem;
    border: solid .0625rem ${Colors.borderLight};
    box-shadow: ${Shadows.xl};
    background: white;
`;

const Panel = React.memo(({ value, children }: { value: string, children: React.ReactNode }) => {
    return <Tabs.Panel value={value}>
        <Flexer height=".5" />
        <Flexer flex="column" height="40">
            {children}
        </Flexer>
    </Tabs.Panel>;
});

export const JiraSidebarWidget = React.memo(({ widget }: { widget: PopoutWidget }) => {

    const subject = widget.subject as JiraCard;
    useComponentObserver(subject);

    const dispatch = useResolve(CommandDispatcher);

    const onClose = useCallback(() => {
        dispatch(jiraSidebarClose);
    }, []);

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: onClose,
    });

    const [activeTab, setActiveTab] = useState<string | null>('desc');

    return <BlockWheel>
        <Frame>
            <Flexer ref={promptRef} flex="column" width="42" padding=".5">
                <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tabs.List>
                        <Tabs.Tab value="desc">Description</Tabs.Tab>
                        <Tabs.Tab value="comments">Comments</Tabs.Tab>
                        <Tabs.Tab value="props">Details</Tabs.Tab>
                    </Tabs.List>
                    <Panel value="desc">
                        <JiraIssueDescriptionView jiraIssueId={subject.record.id} />
                    </Panel>
                    <Panel value="comments">
                        <JiraIssueCommentsView jiraIssueId={subject.record.id} />
                    </Panel>
                    <Panel value="props">
                        <JiraIssueDetailsView jiraIssueId={subject.record.id} />
                    </Panel>
                </Tabs>
            </Flexer>
            <Position at="topRight" inset=".5">
                <CloseButton onClick={onClose} size="lg" />
            </Position>
        </Frame>
    </BlockWheel>;
});
